import { useEffect, useState } from 'react';
import { Show, useShowContext } from 'framework/show';
import { useVacancyGroupService, VACANCY_STATUS } from 'domain/shared/newVacancy';
import { BadgeField, DateField, TextField } from 'framework/ui/field';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import { ShowSection } from 'framework/ui/layout';
import { Stack } from '@mui/material';
import { ControlledList, DataGridLayout } from 'framework/list';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import VacancyGroupInput from 'domain/recruiting/newVacancy/show/vacancy/shared/input/VacancyGroupInput';
import { EditButtonProps, Modal } from 'framework/ui/ui-builder';
import { Alert, Badge, Button } from 'framework/ui/mui-builder';
import { useAuthorization } from 'framework/authorization';
import { ModalForm } from 'framework/form';

const MigrateVacancyButton = ({ permissionToCheck, ...props }: EditButtonProps) => {
  const { data: vacancy } = useShowContext();
  const { mapLegacyVacancy, migrateVacancy } = useVacancyGroupService();
  const [modalOpen, setModalOpen] = useState(false);
  const [mappedVacancy, setMappedVacancy] = useState<any>({});
  const [migrateHiringRequests, setMigrateHiringRequests] = useState(true);
  const [vacancyToMerge, setVacancyToMerge] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setMappedVacancy(await mapLegacyVacancy(vacancy));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeModal = () => {
    setModalOpen(false);
  };

  const openModal = () => {
    setModalOpen(true);
  };

  const { isGranted } = useAuthorization();

  if (permissionToCheck && !isGranted(permissionToCheck)) {
    return null;
  }

  const submit = async () => {
    await migrateVacancy(
      mappedVacancy.group,
      mappedVacancy.vacancies,
      mappedVacancy.publications,
      migrateHiringRequests,
      vacancyToMerge,
    );
  };

  return (
    <>
      <Button {...props} onClick={openModal}>
        Migrate
      </Button>

      <Modal
        backdrop="static"
        isOpen={modalOpen}
        onToggle={closeModal}
        title="Migration plan"
        modalSize="md"
        hasCancelButton
      >
        {!mappedVacancy.error ? (
          <ModalForm closeModal={closeModal} modalOpen={modalOpen} submitFormFunc={submit}>
            <Show getDataMethod={() => mappedVacancy}>
              <ShowSection title="Vacancy Group" titleVariant="h5">
                <Stack direction="row" spacing={4}>
                  <TextField label="Alias" source="group.alias" />
                  <TextField label="Specialization" source="group.specialization" />
                  <DateField label="Created At" source="group.createdAt" />
                  <EmployeeLinkFieldWithNameLoading label="Created By" source="group.createdBy" />
                </Stack>
              </ShowSection>
            </Show>
            <ShowSection title="Vacancies" titleVariant="h5">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={migrateHiringRequests}
                    key="migrate-hiring-requests-checkbox"
                    onChange={(event: any) => setMigrateHiringRequests(event.target.checked)}
                  />
                }
                label="Migrate hiring requests related to this vacancy?"
              />
              {migrateHiringRequests ? (
                <ControlledList data={{ items: mappedVacancy.vacancies ?? [], totalPages: 1 }}>
                  <DataGridLayout>
                    <BadgeField
                      label="Specialization"
                      width={200}
                      badges={[
                        <Badge
                          isVisible={(data: any) => data.status === VACANCY_STATUS.OPEN}
                          sx={{ ml: 1 }}
                          color="primary"
                        >
                          Open
                        </Badge>,
                        <Badge
                          isVisible={(data: any) => data.status === VACANCY_STATUS.CLOSED}
                          sx={{ ml: 1 }}
                          color="default"
                        >
                          Closed
                        </Badge>,
                        <Badge
                          isVisible={(data: any) => data.status === VACANCY_STATUS.CANCELLED}
                          sx={{ ml: 1 }}
                          color="default"
                        >
                          Cancelled
                        </Badge>,
                        <Badge
                          isVisible={(data: any) => data.status === VACANCY_STATUS.REQUIRES_REVIEW}
                          sx={{ ml: 1 }}
                          color="warning"
                        >
                          Requires Review
                        </Badge>,
                      ]}
                    >
                      <TextField source="specialization" />
                    </BadgeField>
                    <TextField label="Competency level" source="competencyLevelMin" />
                    <DateField source="targetHiringDate" />
                    <DateField source="expirationDate" />
                    <TextField source="hiringMode" />
                    <DateField source="createdAt" />
                    <EmployeeLinkFieldWithNameLoading source="createdBy" />
                  </DataGridLayout>
                </ControlledList>
              ) : (
                <VacancyGroupInput
                  label="Vacancy group to move vacancies from:"
                  source="vacancyGroup"
                  defaultFilter={{
                    specialization: {
                      eq: mappedVacancy.group.specialization,
                    },
                  }}
                  onChangeCallback={(data: any) => setVacancyToMerge(data.vacancyGroup)}
                />
              )}
            </ShowSection>
            <ShowSection title="Vacancy Publications" titleVariant="h5">
              <ControlledList data={{ items: mappedVacancy.publications ?? [], totalPages: 1 }}>
                <DataGridLayout>
                  <BadgeField
                    label="Name"
                    width={200}
                    badges={[
                      <Badge
                        isVisible={(data: any) => data.status === VACANCY_STATUS.OPEN}
                        sx={{ ml: 1 }}
                        color="primary"
                      >
                        Open
                      </Badge>,
                      <Badge
                        isVisible={(data: any) => data.status === VACANCY_STATUS.DRAFT}
                        sx={{ ml: 1 }}
                        color="default"
                      >
                        Draft
                      </Badge>,
                    ]}
                  >
                    <TextField source="name" />
                  </BadgeField>
                  <TextField label="Type" source="type" />
                  <TextField label="Country Of Residences" source="countryOfResidences" />
                  <TextField label="English Levels" source="englishLevels" />
                  <DateField label="Craeted At" source="createdAt" />
                  <EmployeeLinkFieldWithNameLoading source="createdBy" />
                </DataGridLayout>
              </ControlledList>
            </ShowSection>
          </ModalForm>
        ) : (
          <>
            <Alert color="warning">{mappedVacancy.error}</Alert>
          </>
        )}
      </Modal>
    </>
  );
};

export default MigrateVacancyButton;
